import * as AccrualTemplatesActions from "@accounting/accrual/accrual-template/_store/accrual-template.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {AccrualTemplate, AccrualTemplateSearchParams} from '@accounting/_common/models/accrual-template.models';
import {AccrualType} from '@accounting/_common/models/_accounting.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const accrualTemplatesFeatureKey = 'accrualTemplates';

const accrualTypeOrder: Record<AccrualType, number> = {
    'MAINTENANCE': 0,
    'COLD_WATER': 1,
    'HOT_WATER': 2,
    'ELECTRICITY': 3,
    'HEATING': 4,
    'GAS': 5
};

export function sortByAccrualType(a: AccrualTemplate, b: AccrualTemplate) {
    if (a.accrualType !== b.accrualType) {
        return accrualTypeOrder[a.accrualType] - accrualTypeOrder[b.accrualType];
    }
    if (a.id && b.id) {
        return a.id.localeCompare(b.id);
    }
    return 0;
}

export const adapter: EntityAdapter<AccrualTemplate> = createEntityAdapter<AccrualTemplate>({
    sortComparer: sortByAccrualType
});

export type AccrualTemplatesState = EntityStateModel<AccrualTemplate, AccrualTemplateSearchParams>;

export const initialState: AccrualTemplatesState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const accrualTemplatesReducer = createReducer(
    initialState,

    on(AccrualTemplatesActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(AccrualTemplatesActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(AccrualTemplatesActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(AccrualTemplatesActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(AccrualTemplatesActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(AccrualTemplatesActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(AccrualTemplatesActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(AccrualTemplatesActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilter = isAvailableByFilter(state.filter, action.payload);
        if (savedItemAvailableByFilter) {
            return adapter.setOne(action.payload!, {
                ...state,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
                pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
            });
        }

        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AccrualTemplatesActions.DeleteItem, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, true)
    })),

    on(AccrualTemplatesActions.DeleteItemSuccess, (state, action) => {
        return adapter.removeOne(action.payload.id!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids, true)}
        });
    }),

    on(AccrualTemplatesActions.DeleteItemFailure, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false)
    })),

    on(AccrualTemplatesActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();

const isAvailableByFilter = (searchParams: AccrualTemplateSearchParams | undefined, document: AccrualTemplate): boolean => {
    if (!searchParams) {
        return true;
    }

    return !(searchParams.accrualType && searchParams.accrualType != document.accrualType);

};
